import { useCallback } from 'react';

import { Currency } from '__generated__/globalTypes';
import {
  MonetaryInput,
  Props as MonetaryInputProps,
} from 'atoms/inputs/MonetaryInput';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useCurrentUserCurrencies } from 'hooks/useCurrentUserCurrencies';
import { MonetaryAmount } from 'lib/monetaryAmount';

import { Field, Props as FieldProps, RenderProps } from '../Field';

type Props = {
  name: string;
  defaultCurrency?: Currency;
  onChange?: (monetaryAmount: MonetaryAmount) => void;
  onToggleEthDisplay?: (ethDisplay: boolean) => void;
  placeholder?: string;
} & Omit<FieldProps<MonetaryAmount>, 'render'> &
  Omit<
    MonetaryInputProps,
    'fiatCurrency' | 'onChange' | 'onlyShowFiatCurrency' | 'cryptocurrency'
  >;

export const MonetaryInputField = ({
  name,
  defaultValue,
  onChange,
  validate,
  ...rest
}: Props) => {
  const {
    walletPreferences: { onlyShowFiatCurrency },
  } = useCurrentUserContext();
  const { fiatCurrency, cryptocurrency } = useCurrentUserCurrencies();

  const render = useCallback(
    ({ error, handleChange, disabled }: RenderProps<MonetaryAmount>) => {
      return (
        <MonetaryInput
          defaultValue={defaultValue}
          fiatCurrency={fiatCurrency}
          cryptocurrency={cryptocurrency}
          onChange={(newMonetaryAmount, target) => {
            onChange?.(newMonetaryAmount);
            handleChange(newMonetaryAmount, target);
          }}
          onlyShowFiatCurrency={onlyShowFiatCurrency}
          error={error}
          {...rest}
          disabled={disabled || rest.disabled}
        />
      );
    },
    [
      defaultValue,
      fiatCurrency,
      cryptocurrency,
      onlyShowFiatCurrency,
      rest,
      onChange,
    ]
  );

  return (
    <Field
      defaultValue={defaultValue}
      name={name}
      validate={validate}
      render={render}
    />
  );
};
